/**
 * Contact delivery helper with a resilient fallback chain.
 *
 *   1. Django backend  (POST /contact/) — PRIMARY path. The backend delivers
 *      through the unified mailer, which uses the server-side EmailJS REST API
 *      (private key) / an HTTP email provider over HTTPS:443, so it works even
 *      on hosts that block outbound SMTP. This is the only path that carries
 *      the private access token, so it is the one EmailJS actually accepts.
 *   2. EmailJS         — client-side delivery over HTTPS. Fallback only. Uses
 *      the public key alone, which many EmailJS accounts reject with a 412
 *      "Precondition Failed" (private key / non-browser required); kept purely
 *      as a best-effort backup when the backend is unreachable.
 *   3. mailto:         — always-available last resort; opens the user's mail
 *      client pre-filled so the request is never lost.
 *
 * Returns which method succeeded so the UI can show an honest confirmation.
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "/api/v1";
const CONTACT_EMAIL =
  (import.meta.env.VITE_CONTACT_EMAIL as string | undefined) || "iancasillasbuffon@gmail.com";

const EMAILJS = {
  service: import.meta.env.VITE_EMAILJS_SERVICE_ID as string | undefined,
  template: import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string | undefined,
  publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string | undefined,
};

export type ContactKind = "engagement" | "contact" | "access" | "demo" | "question";

export interface ContactPayload {
  kind: ContactKind;
  name: string;
  email: string;
  company?: string;
  subject?: string;
  message?: string;
  meta?: Record<string, string | number | string[] | undefined>;
}

export type ContactMethod = "backend" | "emailjs" | "mailto" | "failed";

export interface ContactResult {
  method: ContactMethod;
  ok: boolean;
}

function flattenMeta(meta?: ContactPayload["meta"]): Record<string, string> {
  const out: Record<string, string> = {};
  if (!meta) return out;
  for (const [k, v] of Object.entries(meta)) {
    if (v == null || v === "") continue;
    out[k] = Array.isArray(v) ? v.join(", ") : String(v);
  }
  return out;
}

async function viaBackend(p: ContactPayload, meta: Record<string, string>): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/contact/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...p, meta }),
    });
    if (!res.ok) return false;
    const data = (await res.json().catch(() => null)) as { delivered?: boolean } | null;
    return !!data?.delivered;
  } catch {
    return false;
  }
}

async function viaEmailJs(p: ContactPayload, meta: Record<string, string>): Promise<boolean> {
  if (!EMAILJS.service || !EMAILJS.template || !EMAILJS.publicKey) return false;
  try {
    const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service_id: EMAILJS.service,
        template_id: EMAILJS.template,
        user_id: EMAILJS.publicKey,
        template_params: {
          kind: p.kind,
          name: p.name,
          email: p.email,
          company: p.company ?? "",
          subject: p.subject ?? `Ontoligent ${p.kind} request`,
          message: p.message ?? "",
          to_email: CONTACT_EMAIL,
          details: Object.entries(meta).map(([k, v]) => `${k}: ${v}`).join("\n"),
        },
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function viaMailto(p: ContactPayload, meta: Record<string, string>): boolean {
  const lines = [
    `${p.kind.toUpperCase()} request`,
    "",
    `Name: ${p.name}`,
    `Email: ${p.email}`,
    p.company ? `Company: ${p.company}` : "",
    "",
    p.message ?? "",
    "",
    ...Object.entries(meta).map(([k, v]) => `${k}: ${v}`),
  ].filter(Boolean);
  const subject = p.subject ?? `Ontoligent ${p.kind} request — ${p.company || "new"}`;
  window.location.href =
    `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
  return true;
}

export async function submitContact(p: ContactPayload): Promise<ContactResult> {
  const meta = flattenMeta(p.meta);

  // Backend first: it delivers through the unified server-side mailer (EmailJS
  // private-key REST / HTTP provider on :443), which works on SMTP-blocked
  // hosts and is the only path EmailJS reliably accepts. Client EmailJS and
  // mailto: are best-effort fallbacks if the backend is unreachable.
  if (await viaBackend(p, meta)) return { method: "backend", ok: true };
  if (await viaEmailJs(p, meta)) return { method: "emailjs", ok: true };
  if (viaMailto(p, meta)) return { method: "mailto", ok: true };
  return { method: "failed", ok: false };
}
